<template>
    <div class="page">
        <h1>My Tickets</h1>
        <Tabs
            class="present-past-events"
            :items="tabItems" />
        <div
            class="fetching"
            v-if="isFetching">
            <fx-spinner />
        </div>
        <ul
            class="orders"
            v-else-if="orders.length">
            <li
                class="orders__item"
                v-for="order in orders"
                @click="selectedOrder = order"
                :key="order.id">
                <article class="order">
                    <div
                        class="order__thumbnail"
                        :style="{ backgroundImage: `url(${thumbnailImage(order.event)})` }">
                    </div>
                    <div class="order-content">
                        <header class="order-content__header">
                            <h3 class="order-content__heading order-content__date">
                                <span>Ordered {{ order.date | date('M/D/YY') }}</span>
                                <span class="order-content__quantity fx-hidden--full">
                                    <span class="separator">·</span> Qty: {{ order.ticket.quantity }}
                                </span>
                                <span class="order-content__status--mobile fx-hidden--full">
                                    <order-status :order="order" />
                                </span>
                            </h3>
                            <h2 class="order-content__heading order-content__event-name">{{ order.event.name }}</h2>
                            <h3 class="order-content__heading order-content__event-date">
                                <template
                                    v-if="[ORDER_STATUS.CREATED, ORDER_STATUS.PENDING, ORDER_STATUS.PROCESSED, ORDER_STATUS.SENT_TO_BUYER].includes(order.current_status)"
                                >
                                    <Tag
                                        v-if="order.event.status && (order.event.status !== EVENT_STATUS.SCHEDULED)"
                                        class="order-content__event-status-tag"
                                        context="status"
                                        :text="eventStatusCopy(order)"
                                    />
                                    <span v-if="[EVENT_STATUS.TBD, EVENT_STATUS.POSTPONED].includes(order.event.status)">
                                        Event updates will be emailed
                                    </span>
                                    <span v-else>
                                        <span class="order-content__event-date-weekday">{{ order.event.local_date | date('ddd') }},</span>
                                        <span class="order-content__event-date-month-day">{{ order.event.local_date | date('MMM D') }}</span>
                                        <span class="order-content__event-date-time">at {{ order.event.local_date | date('h:mma') }}</span>
                                    </span>
                                </template>
                                <span v-else>
                                    <span class="order-content__event-date-weekday">{{ order.event.local_date | date('ddd') }},</span>
                                    <span class="order-content__event-date-month-day">{{ order.event.local_date | date('MMM D') }}</span>
                                    <span class="order-content__event-date-time">at {{ order.event.local_date | date('h:mma') }}</span>
                                </span>
                            </h3>
                            <h3 class="order-content__heading order-content__event-venue-name">{{ order.event.venue.name }}, {{ order.event.venue.city_name }}, {{ order.event.venue.subdivision }}</h3>
                        </header>
                        <div class="order-content__details fx-hidden--small fx-hidden--medium fx-hidden--large">
                            <p class="order-content__details-quantity-heading">Qty</p>
                            <strong class="order-content__details-quantity-value">{{ order.ticket.quantity }}</strong>
                        </div>
                        <div class="order-content__status fx-hidden--small fx-hidden--medium fx-hidden--large">
                            <order-status :order="order" />
                        </div>
                        <div class="icon-chevron-lg-right fx-hidden--full">
                        </div>
                    </div>
                </article>
            </li>
        </ul>
        <div v-else>
            <NoResults
                :no-results-copy="`You Have No ${ isUpcoming ? 'Upcoming' : 'Past' } Events`"
                :no-results-secondary-copy="`${ isUpcoming ? 'When you purchase tickets, you can track all your future events right here.': 'After your event has passed, your past orders will live here for your reference.' }`"
                redirect-copy="Choose Your Next Event"
            />
        </div>
        <fxModal
            class="order-details"
            size="large"
            :open="true"
            @close="selectedOrder = null"
            v-if="selectedOrder">
            <OrderCard
                is-show-order-date
                :order="selectedOrder"
            />
        </fxModal>
    </div>
</template>

<script>
import { EVENT_STATUS, ORDER_STATUS } from '../constants.js'
import errorHandleable from '../mixins/ErrorHandleable'
import fxModal from 'core/components/ModalComponent.vue'
import fxSpinner from 'core/components/SpinnerComponent.vue'
import NoResults from '../components/NoResults.vue'
import OrderCard from '../components/OrderCard.vue'
import orderStatus from '../components/OrderStatus.vue'
import quebecUserModal from '../mixins/QuebecUserModal'
import resizable from '../mixins/Resizable'
import Tabs from '../components/Tabs.vue'
import Tag from 'core/components/TagComponent.vue'
import trackable from '../mixins/Trackable'

export default {
    beforeCreate () {
        this.htmlPageTitle = `Your Tickets at ${this.$config.partnerName}`
    },
    components: {
        fxModal,
        fxSpinner,
        NoResults,
        OrderCard,
        orderStatus,
        Tabs,
        Tag
    },
    computed: {
        orders () {
            return this.isUpcoming ? this.allOrders.upcoming : this.allOrders.past
        },
        tabItems () {
            return [
                {
                    isActive: this.isUpcoming,
                    label: 'Upcoming Events',
                    to: { name: 'purchases-upcoming' }
                },
                {
                    isActive: !this.isUpcoming,
                    label: 'Past Events',
                    to: { name: 'purchases-past' }
                }
            ]
        }
    },
    created () {
        this.EVENT_STATUS = EVENT_STATUS
        this.ORDER_STATUS = ORDER_STATUS

        this.isFetching = true
        this.$marketplace
            .getOrderHistory(this.$store.state.userProfile.id)
            .then(response => {
                this.allOrders = response
            })
            .catch(err => this.catchAndLog(err))
            .finally(() => {
                this.isFetching = false
            })

        this.trackPageView(this.$route.fullPath, this.htmlPageTitle)
    },
    data () {
        return {
            allOrders: {
                past: [],
                upcoming: []
            },
            isFetching: false,
            isUpcoming: false,
            selectedOrder: null
        }
    },
    head: {
        title () {
            return {
                inner: this.htmlPageTitle
            }
        }
    },
    methods: {
        eventStatusCopy (order) {
            if (order.event.status === EVENT_STATUS.TBD) { return 'Date TBD' }
            return order.event.status
        },
        thumbnailImage (event) {
            return this.isWindowSmall ? (event.images.rectangle || event.images.square).url : (event.images.square || event.images.rectangle).url
        }
    },
    mixins: [
        errorHandleable,
        quebecUserModal,
        resizable,
        trackable
    ],
    watch: {
        $route: {
            immediate: true,
            handler () {
                this.isUpcoming = this.$route.name === 'purchases-upcoming'
            }
        }
    }
}
</script>

<style lang="postcss" scoped>
@import 'theme';

h1 {
    font-size: 22px;
    font-weight: 500;
    margin: 0 0 16px;
}

.fetching {
    height: 200px;
    position: relative;
}

.no-results-secondary {
    color: #444649;
    font-size: 16px;
    margin-top: 8px;

    & .no-results-secondary__action {
        text-decoration: underline;

        &:hover {
            text-decoration: none;
        }
    }
}

.order {
    &:focus,
    &:hover {
        box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0.25);
        outline: none;
        transform: scale(1.01);
        transition: 0.2s ease;
    }
    @media (--mq-large-min) {
        display: flex;
    }

    & .order__thumbnail {
        background-position: 50% 50%;
        background-repeat: no-repeat;
        background-size: cover;
        border-radius: 4px 4px 0 0;
        box-shadow: 0 1px 4px 0 rgba(0,0,0,0.2);
        height: 164px;

        @media (--mq-large-min) {
            border-radius: 4px 0 0 4px;
            height: 116px;
            width: 116px;

        }
    }
}

.order-content {
    flex: 1;
    align-items: center;
    background: white;
    border-radius: 0 0 4px 4px;
    box-shadow: 0 1px 4px 0 rgba(0,0,0,0.2);
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 8px 0;
    transition: 0.2s ease;

    @media (--mq-large-min) {
        height: 116px;
        padding: 17px 37px 17px 24px;
        border-radius: 0 4px 4px 0px;
    }

    & .order-content__event-status-tag {
        margin-right: 8px;
    }

    & .order-content__event-status {
      white-space: nowrap;
    }

    & .icon-chevron-lg-right {
        color: var(--c-primary);
        padding-right: 8px;
        font-size: var(--fs-larger);
    }

    & .order-content__quantity {
        font-size: 12px;
        & .separator {
            font-size: var(--fs-normal);
            font-weight: bold;
            line-height: 1em;
            margin-left: 4px;
        }
    }

    & .order-content__details {
        font-size: 14px;
        min-width: 35px;
        text-align: center;
    }

    & .order-content__details-quantity-heading {
        color: #808689;
        font-size: var(--fs-small);
        font-weight: var(--fw-bold);
        margin: 0;
    }

    & .order-content__details-quantity-value {
        color: #444649;
        font-size: var(--fs-large);
        font-weight: 600;
    }

    & .order-content__details-mobile {
        font-weight: 500;
        margin: 10px 0;
    }

    & .order-content__header {
        flex: 1 1 auto;
        margin: 0 20px;
        @media (--mq-large-min) {
            margin: 0;
        }
    }

    & .order-content__heading {
        font-size: var(--fs-small);
        font-weight: 400;
        line-height: 1.5em;
        margin: 0;
        @media (--mq-large-min) {
            font-size: var(--fs-normal);
        }
        &.order-content__date {
            display: flex;
            font-size: var(--fs-small);
            font-style: italic;
            font-weight: 100;
        }

        &.order-content__event-name {
            color: #444649;
            font-size: 16px;
            font-weight: 500;
            padding: 8px 0;
            @media (--mq-large-min) {
                font-size: var(--fs-large);
                padding: 0;
            }
        }

        &.order-content__event-date {
            color: #353639;
            font-size: var(--fs-normal);
            white-space: nowrap;

            & .order-content__event-date-time {
                color: #6C7275;
                font-weight: 100;
            }
        }

        &.order-content__event-venue-name {
            color: #6C7275;
            font-size: 14px;
            font-weight: 100;
        }
    }

    & .order-content__status {
        & .order-status {
            font-size: var(--fs-large);
            font-weight: var(--fw-normal);
        }
        flex: 0 0 140px;
        min-width: 175px;
        text-align: right;
    }
    & .order-content__status--mobile {
        margin-left: 24px;
        & .order-status {
            font-size: var(--fs-normal);
            font-style: normal;
            font-weight: var(--fw-normal);
        }
    }
}

.orders {
    list-style: none;
    margin: 0;
    padding: 0;

    & .orders__item {
        cursor: pointer;
        margin-bottom: 10px;
    }
}

.order-details {
    & >>> .heading {
        & h2 {
            font-weight: 100;
            margin-bottom: 0;
            padding-left: 15px;
        }
    }

    & >>> .modal {
        & .content {
            padding: 0;
        }
    }
}

.page {
    margin: 0 auto;
    max-width: 100%;
    padding: 48px var(--site-padding);
    width: var(--site-container-width);
}

.present-past-events {
    margin-bottom: 24px;
}

</style>
